import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Trophy, Users, Rss, Settings as Cog, Crown, Loader2, Copy, Check,
  Trash2, LogOut, UserMinus, ShieldCheck, ShieldOff, PartyPopper, UserPlus, X, Lock, Globe,
  BarChart3, Eye, EyeOff, ShieldQuestion,
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METRIC_LABELS = { applications: "Applications", optimizations: "Optimizations", shortlisted: "Shortlisted", interviews: "Interviews", offers: "Offers" };
const ALL_METRICS = ["applications", "optimizations", "shortlisted", "interviews", "offers"];
const CHART_COLORS = { applications: "#6366f1", optimizations: "#06b6d4", shortlisted: "#f59e0b", interviews: "#8b5cf6", offers: "#10b981" };
const RANGES = [{ k: "day", l: "Today" }, { k: "week", l: "7d" }, { k: "15day", l: "15d" }, { k: "month", l: "30d" }, { k: "all", l: "All" }];

const Toggle = ({ on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    style={{ width: 44, height: 25, borderRadius: 999, border: "none", cursor: "pointer", flexShrink: 0,
      background: on ? "#6366f1" : "var(--border-main)", position: "relative", transition: "background 0.15s" }}>
    <span style={{ position: "absolute", top: 3, left: on ? 22 : 3, width: 19, height: 19, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
  </button>
);

// Compact toggle/segment chip (avoids the full-width .btn-primary style)
const chip = (active) => ({
  padding: "0.42rem 0.9rem", borderRadius: 999, fontSize: "0.82rem", cursor: "pointer",
  border: `1.5px solid ${active ? "#6366f1" : "var(--border-main)"}`,
  background: active ? "rgba(99,102,241,0.15)" : "transparent",
  color: active ? "#6366f1" : "var(--text-secondary)",
  fontWeight: active ? 600 : 400, transition: "all 0.15s",
  display: "inline-flex", alignItems: "center", gap: 6,
});

export default function SquadDetailPage() {
  const { squadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [squad, setSquad] = useState(null);
  const [tab, setTab] = useState("leaderboard");
  const [loading, setLoading] = useState(true);
  const [lb, setLb] = useState(null);
  const [lbRange, setLbRange] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [memberMetric, setMemberMetric] = useState(null);
  const [anRange, setAnRange] = useState("");
  const [myPrefs, setMyPrefs] = useState(null);
  const [feed, setFeed] = useState([]);
  const [requests, setRequests] = useState([]);
  const [copied, setCopied] = useState(false);
  const [edit, setEdit] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const isAdmin = squad?.my_role === "admin";
  const isCreator = squad?.created_by === user?.id;

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/squads/${squadId}`);
      setSquad(res.data);
      setEdit({
        name: res.data.name, description: res.data.description, privacy: res.data.privacy,
        leaderboard_config: { ...res.data.leaderboard_config },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Can't open this squad");
      navigate("/app/squads");
    } finally { setLoading(false); }
  }, [squadId, navigate]);
  useEffect(() => { load(); }, [load]);

  const loadLb = useCallback(async (r) => {
    try {
      const res = await axios.get(`${API}/squads/${squadId}/leaderboard${r ? `?range=${r}` : ""}`);
      setLb(res.data); setLbRange(res.data.range);
    } catch { /* ignore */ }
  }, [squadId]);
  useEffect(() => { if (squad && tab === "leaderboard") loadLb(lbRange); }, [squad, tab, loadLb]); // eslint-disable-line

  const loadAnalytics = useCallback(async (r) => {
    try {
      const res = await axios.get(`${API}/squads/${squadId}/analytics${r ? `?range=${r}` : ""}`);
      setAnalytics(res.data); setAnRange(res.data.range);
    } catch { /* ignore */ }
  }, [squadId]);
  useEffect(() => { if (squad && tab === "analytics") loadAnalytics(anRange); }, [squad, tab, loadAnalytics]); // eslint-disable-line

  useEffect(() => {
    if (squad && tab === "privacy") {
      axios.get(`${API}/squads/${squadId}/my-prefs`).then((r) => setMyPrefs(r.data.share_prefs)).catch(() => {});
    }
  }, [squad, tab, squadId]);

  const togglePref = async (k) => {
    const next = { ...myPrefs, [k]: !myPrefs[k] };
    setMyPrefs(next);
    try { await axios.put(`${API}/squads/${squadId}/my-prefs`, { share_prefs: { [k]: next[k] } }); toast.success("Privacy updated"); }
    catch { toast.error("Failed to save"); setMyPrefs(myPrefs); }
  };

  useEffect(() => {
    if (!squad) return;
    if (tab === "feed") axios.get(`${API}/squads/${squadId}/feed`).then((r) => setFeed(r.data.events || [])).catch(() => {});
    if (tab === "requests" && isAdmin) axios.get(`${API}/squads/${squadId}/requests`).then((r) => setRequests(r.data.requests || [])).catch(() => {});
  }, [tab, squad, squadId, isAdmin]);

  const copyCode = (link) => {
    const text = link ? `${window.location.origin}/app/squads?code=${squad.invite_code}` : squad.invite_code;
    navigator.clipboard.writeText(text);
    setCopied(true); toast.success(link ? "Invite link copied" : "Code copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const saveSettings = async () => {
    setSavingCfg(true);
    try {
      const res = await axios.put(`${API}/squads/${squadId}`, edit);
      setSquad((s) => ({ ...s, ...res.data }));
      toast.success("Squad updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSavingCfg(false); }
  };

  const toggleMetric = (mkey) => {
    const cur = edit.leaderboard_config.metrics || [];
    const next = cur.includes(mkey) ? cur.filter((x) => x !== mkey) : [...cur, mkey];
    if (next.length === 0) return;
    let primary = edit.leaderboard_config.primary_metric;
    if (!next.includes(primary)) primary = next[0];
    setEdit({ ...edit, leaderboard_config: { ...edit.leaderboard_config, metrics: next, primary_metric: primary } });
  };

  const doLeave = async () => {
    if (!window.confirm("Leave this squad?")) return;
    try { await axios.post(`${API}/squads/${squadId}/leave`); toast.success("Left squad"); navigate("/app/squads"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this squad permanently? This cannot be undone.")) return;
    try { await axios.delete(`${API}/squads/${squadId}`); toast.success("Squad deleted"); navigate("/app/squads"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const removeMember = async (uid) => {
    try { await axios.post(`${API}/squads/${squadId}/members/${uid}/remove`); toast.success("Member removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const setRole = async (uid, role) => {
    try { await axios.post(`${API}/squads/${squadId}/members/${uid}/role`, { role }); toast.success(`Set as ${role}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const handleReq = async (reqId, action) => {
    try {
      await axios.post(`${API}/squads/${squadId}/requests/${reqId}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setRequests((rs) => rs.filter((r) => r.id !== reqId)); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="analytics-loading" data-testid="squad-loading"><div className="processing-spinner" /></div>;
  if (!squad) return null;

  const tabs = [
    { k: "leaderboard", l: "Leaderboard", icon: Trophy },
    { k: "analytics", l: "Analytics", icon: BarChart3 },
    { k: "members", l: `Members (${squad.member_count})`, icon: Users },
    { k: "feed", l: "Feed", icon: Rss },
    { k: "privacy", l: "My Privacy", icon: ShieldQuestion },
    ...(isAdmin ? [{ k: "requests", l: `Requests${squad.pending_requests ? ` (${squad.pending_requests})` : ""}`, icon: UserPlus }] : []),
    ...(isAdmin ? [{ k: "settings", l: "Settings", icon: Cog }] : []),
  ];

  return (
    <div className="page-container fade-in" data-testid="squad-detail-page">
      <button className="btn-secondary" data-testid="squad-back-btn" onClick={() => navigate("/app/squads")}><ArrowLeft size={16} /> Squads</button>

      <div className="page-header" style={{ marginTop: "0.8rem" }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {squad.name}
            {squad.privacy === "request" ? <Lock size={16} /> : <Globe size={16} />}
            {isAdmin && <span style={{ fontSize: "0.66rem", padding: "2px 8px", borderRadius: 999, background: "rgba(245,158,11,0.15)", color: "#f59e0b", display: "inline-flex", alignItems: "center", gap: 3 }}><Crown size={11} /> Admin</span>}
          </h1>
          {squad.description && <p style={{ color: "var(--text-secondary)" }}>{squad.description}</p>}
        </div>
        {!isCreator && <button className="btn-secondary" data-testid="leave-squad-btn" onClick={doLeave}><LogOut size={15} /> Leave</button>}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid var(--border-main)", marginBottom: "1.2rem" }}>
        {tabs.map(({ k, l, icon: Icon }) => (
          <button key={k} data-testid={`squad-tab-${k}`} onClick={() => setTab(k)}
            style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === k ? "#6366f1" : "transparent"}`,
              color: tab === k ? "var(--text-primary)" : "var(--text-secondary)", padding: "0.6rem 0.9rem", cursor: "pointer",
              fontWeight: tab === k ? 600 : 400, display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem" }}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      {/* LEADERBOARD */}
      {tab === "leaderboard" && (
        <div data-testid="leaderboard-tab">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1rem" }}>
            {RANGES.map((r) => (
              <button key={r.k} data-testid={`lb-range-${r.k}`} onClick={() => { setLbRange(r.k); loadLb(r.k); }}
                style={chip(lbRange === r.k)}>{r.l}</button>
            ))}
          </div>
          {!lb ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div> : (
            <div className="card" style={{ padding: 0, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }} data-testid="leaderboard-table">
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                    <th style={{ padding: "0.8rem 1rem" }}>#</th>
                    <th style={{ padding: "0.8rem 1rem" }}>Member</th>
                    {(lb.config.metrics || ALL_METRICS).map((m) => (
                      <th key={m} style={{ padding: "0.8rem 1rem", textAlign: "right", color: m === lb.config.primary_metric ? "#6366f1" : undefined }}>
                        {METRIC_LABELS[m]}{m === lb.config.primary_metric ? " ★" : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lb.rows.map((r, i) => (
                    <tr key={r.user_id} data-testid={`lb-row-${r.username}`} style={{ borderTop: "1px solid var(--border-main)" }}>
                      <td style={{ padding: "0.7rem 1rem", fontWeight: 600 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</td>
                      <td style={{ padding: "0.7rem 1rem" }}>
                        <span style={{ cursor: "pointer", fontWeight: 500 }} onClick={() => navigate(`/app/u/${r.username}`)}>{r.name || r.username}</span>
                        {r.role === "admin" && <Crown size={12} style={{ marginLeft: 5, color: "#f59e0b" }} />}
                        <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>@{r.username}</div>
                      </td>
                      {(lb.config.metrics || ALL_METRICS).map((m) => (
                        <td key={m} style={{ padding: "0.7rem 1rem", textAlign: "right", fontWeight: m === lb.config.primary_metric ? 700 : 400, color: r.metrics[m] === null ? "var(--text-muted)" : (m === lb.config.primary_metric ? "#6366f1" : undefined) }}>{r.metrics[m] === null ? "—" : (r.metrics[m] ?? 0)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ANALYTICS */}
      {tab === "analytics" && (
        <div data-testid="analytics-tab">
          <h3 style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 0 }}><BarChart3 size={18} /> Team performance</h3>
          <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>How your squad is performing as a team — totals, activity mix, hiring funnel, and how each member compares.</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1rem" }}>
            {RANGES.map((r) => (
              <button key={r.k} data-testid={`an-range-${r.k}`} onClick={() => { setAnRange(r.k); loadAnalytics(r.k); }} style={chip(anRange === r.k)}>{r.l}</button>
            ))}
          </div>
          {!analytics ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div> : (
            <>
              {/* KPI cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "0.8rem", marginBottom: "1.4rem" }}>
                {analytics.metrics.map((m) => (
                  <div key={m} className="card" data-testid={`an-kpi-${m}`} style={{ padding: "1rem" }}>
                    <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>{METRIC_LABELS[m]}</div>
                    <div style={{ fontSize: "1.7rem", fontWeight: 700, marginTop: 2, color: CHART_COLORS[m] }}>{analytics.totals[m] ?? 0}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem" }}>
                {/* Pie: metric mix */}
                <div className="card" data-testid="an-pie" style={{ padding: "1.2rem" }}>
                  <h3 style={{ marginTop: 0, fontSize: "0.95rem" }}>Activity mix</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={analytics.metrics.map((m) => ({ name: METRIC_LABELS[m], value: analytics.totals[m] || 0, key: m }))}
                        dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                        {analytics.metrics.map((m) => <Cell key={m} fill={CHART_COLORS[m]} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Funnel (bar) */}
                {analytics.funnel.length > 0 && (
                  <div className="card" data-testid="an-funnel" style={{ padding: "1.2rem" }}>
                    <h3 style={{ marginTop: 0, fontSize: "0.95rem" }}>Hiring funnel</h3>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={analytics.funnel} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} width={80} />
                        <Tooltip /><Bar dataKey="value" radius={[0, 6, 6, 0]}>
                          {analytics.funnel.map((f) => <Cell key={f.key} fill={CHART_COLORS[f.key] || "#6366f1"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Per-member comparison bar (selectable metric) */}
              <div className="card" data-testid="an-members-bar" style={{ padding: "1.2rem", marginTop: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Team performance by member</h3>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {analytics.metrics.map((m) => (
                      <button key={m} data-testid={`an-member-metric-${m}`} onClick={() => setMemberMetric(m)}
                        style={chip((memberMetric || analytics.primary_metric) === m)}>{METRIC_LABELS[m]}</button>
                    ))}
                  </div>
                </div>
                {(() => { const mm = memberMetric || analytics.primary_metric; return (
                  <ResponsiveContainer width="100%" height={Math.max(220, analytics.per_member.length * 38)}>
                    <BarChart data={analytics.per_member.map((p) => ({ name: p.name || p.username, value: p.metrics[mm] || 0 }))} layout="vertical" margin={{ left: 30 }}>
                      <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                      <Tooltip /><Bar dataKey="value" fill={CHART_COLORS[mm] || "#6366f1"} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ); })()}
              </div>
              <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 10 }}>Stats respect each member's privacy choices. Admins pick which metrics appear here in Settings → Leaderboard display.</p>
            </>
          )}
        </div>
      )}

      {/* MY PRIVACY */}
      {tab === "privacy" && (
        <div data-testid="privacy-tab">
          <div className="card" style={{ padding: "1.3rem" }}>
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><ShieldQuestion size={18} /> What you share with this Squad</h3>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>Toggle off any stat you'd rather keep private here. Hidden stats won't appear in this Squad's leaderboard or analytics (other Squads are unaffected).</p>
            {!myPrefs ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>
              : ALL_METRICS.map((m, i) => (
                <div key={m} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.8rem 0", borderTop: i ? "1px solid var(--border-main)" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {myPrefs[m] ? <Eye size={16} style={{ color: "#10b981" }} /> : <EyeOff size={16} style={{ color: "var(--text-muted)" }} />}
                    <div>
                      <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>{METRIC_LABELS[m]}</div>
                      <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>{myPrefs[m] ? "Visible to this Squad" : "Hidden from this Squad"}</div>
                    </div>
                  </div>
                  <Toggle on={!!myPrefs[m]} onClick={() => togglePref(m)} testid={`pref-${m}`} />
                </div>
              ))}
          </div>
        </div>
      )}
      {tab === "members" && (
        <div data-testid="members-tab">
          {squad.members.map((m) => (
            <div key={m.user_id} className="card" data-testid={`member-${m.username}`}
              style={{ padding: "0.8rem 1.1rem", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--border-main)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%" }} /> : <Users size={16} />}
                </div>
                <div>
                  <div style={{ fontWeight: 500, cursor: "pointer" }} onClick={() => navigate(`/app/u/${m.username}`)}>{m.name || m.username}
                    {m.role === "admin" && <Crown size={12} style={{ marginLeft: 5, color: "#f59e0b" }} />}
                    {squad.created_by === m.user_id && <span style={{ fontSize: "0.66rem", marginLeft: 6, color: "var(--text-muted)" }}>creator</span>}
                  </div>
                  <div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>@{m.username}</div>
                </div>
              </div>
              {isAdmin && m.user_id !== user?.id && squad.created_by !== m.user_id && (
                <div style={{ display: "flex", gap: 6 }}>
                  {m.role === "admin"
                    ? <button className="btn-secondary" data-testid={`demote-${m.username}`} onClick={() => setRole(m.user_id, "member")} title="Demote"><ShieldOff size={14} /></button>
                    : <button className="btn-secondary" data-testid={`promote-${m.username}`} onClick={() => setRole(m.user_id, "admin")} title="Make admin"><ShieldCheck size={14} /></button>}
                  <button className="btn-secondary" data-testid={`remove-${m.username}`} onClick={() => removeMember(m.user_id)} title="Remove" style={{ color: "#ef4444" }}><UserMinus size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* FEED */}
      {tab === "feed" && (
        <div data-testid="feed-tab">
          {feed.length === 0 ? <div className="card" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}><Rss size={32} style={{ opacity: 0.35 }} /><p>No activity yet. Wins and new members will show up here.</p></div>
            : feed.map((e) => (
              <div key={e.id} className="card" data-testid={`feed-${e.id}`} style={{ padding: "0.9rem 1.1rem", marginBottom: 8, display: "flex", gap: 10, alignItems: "flex-start" }}>
                {e.type === "celebration"
                  ? <PartyPopper size={20} style={{ color: "#10b981", flexShrink: 0 }} />
                  : <UserPlus size={20} style={{ color: "#6366f1", flexShrink: 0 }} />}
                <div>
                  {e.type === "celebration"
                    ? <div><strong>{e.name}</strong> landed <strong>{e.job_title || "a new role"}</strong>{e.company ? ` at ${e.company}` : ""} 🎉{e.message && <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: 3 }}>“{e.message}”</div>}</div>
                    : <div><strong>{e.name}</strong> joined the squad</div>}
                  <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 3 }}>{new Date(e.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* REQUESTS */}
      {tab === "requests" && isAdmin && (
        <div data-testid="requests-tab">
          {requests.length === 0 ? <div className="card" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>No pending requests.</div>
            : requests.map((r) => (
              <div key={r.id} className="card" data-testid={`request-${r.username}`} style={{ padding: "0.8rem 1.1rem", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div><div style={{ fontWeight: 500 }}>{r.name}</div><div style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>@{r.username}</div></div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn-primary" data-testid={`approve-${r.username}`} onClick={() => handleReq(r.id, "approve")}><Check size={14} /> Approve</button>
                  <button className="btn-secondary" data-testid={`reject-${r.username}`} onClick={() => handleReq(r.id, "reject")}><X size={14} /></button>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* SETTINGS */}
      {tab === "settings" && isAdmin && edit && (
        <div data-testid="settings-tab">
          {/* Invite */}
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0 }}>Invite</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <code style={{ background: "var(--bg-surface-hl)", padding: "0.5rem 0.9rem", borderRadius: 8, fontSize: "1rem", letterSpacing: 1 }} data-testid="invite-code">{squad.invite_code}</code>
              <button className="btn-secondary" data-testid="copy-code-btn" onClick={() => copyCode(false)}>{copied ? <Check size={14} /> : <Copy size={14} />} Copy code</button>
              <button className="btn-secondary" data-testid="copy-link-btn" onClick={() => copyCode(true)}><Copy size={14} /> Copy link</button>
            </div>
          </div>

          {/* Details */}
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0 }}>Details</h3>
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Name</label>
            <input className="modal-input" data-testid="edit-name-input" value={edit.name} maxLength={60} onChange={(e) => setEdit({ ...edit, name: e.target.value })} style={{ width: "100%", marginBottom: 10 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Description</label>
            <textarea className="modal-input" data-testid="edit-desc-input" value={edit.description} rows={2} maxLength={300} onChange={(e) => setEdit({ ...edit, description: e.target.value })} style={{ width: "100%", marginBottom: 10, resize: "vertical" }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)", display: "block", marginBottom: 6 }}>Privacy</label>
            <div style={{ display: "flex", gap: 8 }}>
              {["open", "request"].map((v) => (
                <button key={v} data-testid={`edit-privacy-${v}`} onClick={() => setEdit({ ...edit, privacy: v })}
                  style={{ ...chip(edit.privacy === v), textTransform: "capitalize" }}>{v === "open" ? "Open" : "Request to join"}</button>
              ))}
            </div>
          </div>

          {/* Leaderboard config */}
          <div className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0 }}>Leaderboard display</h3>
            <p style={{ fontSize: "0.83rem", color: "var(--text-secondary)", marginTop: 0 }}>
              Choose which stat <strong>columns</strong> everyone sees, how members are <strong>ranked</strong>, and the default <strong>time window</strong>. Tap to toggle, then hit <strong>Save</strong>.
            </p>

            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)", display: "block", margin: "0.6rem 0 6px" }}>1 · Columns to show (tap to add/remove)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {ALL_METRICS.map((m) => {
                const on = edit.leaderboard_config.metrics.includes(m);
                return (
                  <button key={m} data-testid={`cfg-metric-${m}`} onClick={() => toggleMetric(m)} style={chip(on)}>
                    {on ? <Check size={13} /> : <X size={13} style={{ opacity: 0.5 }} />}{METRIC_LABELS[m]}
                  </button>
                );
              })}
            </div>

            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)", display: "block", marginBottom: 6 }}>2 · Rank members by</label>
            <select className="modal-input" data-testid="cfg-primary-select" value={edit.leaderboard_config.primary_metric}
              onChange={(e) => setEdit({ ...edit, leaderboard_config: { ...edit.leaderboard_config, primary_metric: e.target.value } })}
              style={{ width: 240, marginBottom: 16 }}>
              {edit.leaderboard_config.metrics.map((m) => <option key={m} value={m}>{METRIC_LABELS[m]}</option>)}
            </select>

            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)", display: "block", marginBottom: 6 }}>3 · Default time window</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
              {RANGES.map((r) => (
                <button key={r.k} data-testid={`cfg-tf-${r.k}`} onClick={() => setEdit({ ...edit, leaderboard_config: { ...edit.leaderboard_config, timeframe: r.k } })}
                  style={chip(edit.leaderboard_config.timeframe === r.k)}>{r.l}</button>
              ))}
            </div>

            <div data-testid="lb-config-summary" style={{ fontSize: "0.82rem", color: "var(--text-primary)", background: "var(--bg-surface-hl)", borderRadius: 8, padding: "0.7rem 0.9rem", display: "flex", alignItems: "center", gap: 8 }}>
              <Trophy size={15} style={{ color: "#6366f1" }} />
              <span>Ranked by <strong>{METRIC_LABELS[edit.leaderboard_config.primary_metric]}</strong> over <strong>{RANGES.find((r) => r.k === edit.leaderboard_config.timeframe)?.l}</strong> · showing {edit.leaderboard_config.metrics.length} column{edit.leaderboard_config.metrics.length !== 1 ? "s" : ""}.</span>
            </div>
          </div>

          <button className="btn-primary" data-testid="save-settings-btn" onClick={saveSettings} disabled={savingCfg}>
            {savingCfg ? <Loader2 size={16} className="spin" /> : <Check size={16} />} Save settings
          </button>

          {isCreator && (
            <div className="card" style={{ padding: "1.3rem", marginTop: "1.5rem", borderColor: "rgba(239,68,68,0.4)" }}>
              <h3 style={{ marginTop: 0, color: "#ef4444" }}>Danger zone</h3>
              <button data-testid="delete-squad-btn" onClick={doDelete} className="btn-secondary" style={{ color: "#ef4444", borderColor: "rgba(239,68,68,0.5)" }}><Trash2 size={15} /> Delete squad</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
